import { useControlledValue, useLatest } from '@react-native-molecules/utils/hooks';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';
import { useOptionalDatePickerContext, type DatePickerLocale } from 'react-native-molecules/components/DatePicker';
import { addMonths, getInitialIndex, getRealIndex } from 'react-native-molecules/components/DatePickerInline/dateUtils';
import Month from 'react-native-molecules/components/DatePickerInline/Month';
import {
    defaultValue,
    Provider,
    useDatePickerInlineStore,
} from 'react-native-molecules/components/DatePickerInline/store';
import Swiper from 'react-native-molecules/components/DatePickerInline/Swiper';
import type { CalendarDate, SingleChange, ValidRangeType } from 'react-native-molecules/components/DatePickerInline';
import { datePickerStyles } from 'react-native-molecules/components/DatePickerInline';
import { CalendarMonthGrid, CalendarYearGrid } from './CalendarPickerGrid';
import DockedHeader from './DockedHeader';

type DateFieldPickerInlineProps = {
    containerStyle?: ViewStyle;
    date?: CalendarDate;
    locale?: DatePickerLocale;
    onChange?: SingleChange;
    showOutsideDays?: boolean;
    validRange?: ValidRangeType;
};

const DateFieldPickerInline = ({
    containerStyle: containerStyleProp,
    date,
    locale = 'en',
    onChange,
    showOutsideDays = true,
    validRange,
}: DateFieldPickerInlineProps) => {
    const onInnerChange = useCallback(
        (params: { date: CalendarDate }) => {
            onChange?.(params);
        },
        [onChange]
    );

    const [state, onStateChange] = useControlledValue<{ date: CalendarDate }>({
        value: date !== undefined ? { date } : undefined,
        onChange: onInnerChange,
    });

    return (
        <View style={[datePickerStyles.root, containerStyleProp]}>
            <Provider value={defaultValue}>
                <DateFieldPickerInlineBase
                    date={state?.date}
                    locale={locale}
                    onChange={onStateChange}
                    showOutsideDays={showOutsideDays}
                    validRange={validRange}
                />
            </Provider>
        </View>
    );
};

type DateFieldPickerInlineBaseProps = {
    date?: CalendarDate;
    locale?: DatePickerLocale;
    onChange?: SingleChange;
    showOutsideDays?: boolean;
    validRange?: ValidRangeType;
};

function DateFieldPickerInlineBase({
    date,
    locale = 'en',
    onChange,
    showOutsideDays,
    validRange,
}: DateFieldPickerInlineBaseProps) {
    const [pickerType, setStore] = useDatePickerInlineStore((state) => state.pickerType);
    const onChangeRef = useLatest(onChange);

    const nowYear = new Date().getFullYear();
    useEffect(() => {
        setStore(() => ({
            localDate: date || new Date(),
            startDateYear: nowYear - 100,
            endDateYear: nowYear + 100,
        }));
    }, []);

    const onPressDate = useCallback(
        (nextDate: Date) => {
            onChangeRef.current?.({ date: nextDate });
        },
        [onChangeRef]
    );

    const { containerStyle, initialIndex } = useMemo(
        () => ({
            containerStyle: styles.root,
            initialIndex: getInitialIndex(date ?? undefined),
        }),
        [date]
    );

    const renderMonthComponent = useCallback(
        (index: number) => (
            <View role="group" accessibilityLabel={new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(addMonths(new Date(), getRealIndex(index)))}>
            <Month
                date={date}
                index={index}
                locale={locale}
                mode="single"
                onPressDate={onPressDate}
                scrollMode="horizontal"
                showOutsideDays={showOutsideDays}
                validRange={validRange}
            />
            </View>
        ),
        [date, locale, onPressDate, showOutsideDays, validRange]
    );

    const renderHeader = useCallback(() => <DockedHeader locale={locale} scrollMode="horizontal" />, [locale]);

    return (
        <View style={containerStyle}>
            <Swiper
                initialIndex={initialIndex}
                renderHeader={renderHeader}
                renderItem={renderMonthComponent}
                scrollMode="horizontal"
            />
            {pickerType === 'month' && <CalendarMonthGrid locale={locale} />}
            {pickerType === 'year' && <CalendarYearGrid />}
        </View>
    );
}

export const DateFieldCalendar = memo(({ containerStyle }: { containerStyle?: ViewStyle }) => {
    const ctx = useOptionalDatePickerContext();

    if (!ctx) return null;

    const date =
        ctx.draftValue && typeof ctx.draftValue === 'object' && 'start' in ctx.draftValue ? null : ctx.draftValue;

    const handleChange: SingleChange = ({ date: nextDate }) => {
        ctx.setValue(nextDate ?? null);
    };

    return (
        <DateFieldPickerInline
            containerStyle={containerStyle}
            date={date ?? undefined}
            locale={ctx.locale}
            onChange={handleChange}
            showOutsideDays
            validRange={ctx.validRange}
        />
    );
});

DateFieldCalendar.displayName = 'DateFieldCalendar';

const styles = StyleSheet.create({
    root: {
        position: 'relative',
    },
});
